/* eslint-disable max-classes-per-file */
import { DiagnosticConfig } from '@/api/diagnostic';
import {
    readSample, Sample, summarize, Summary,
} from './summary';

export type DiagnosticStep = 'register' | 'call' | 'measure';
export type DiagnosticFailure = 'register' | 'call';

export class DiagnosticError extends Error {
    constructor(readonly kind: DiagnosticFailure, readonly detail = '') {
        super(kind);
    }
}

/** El usuario cortó la medición: no es un error de la conexión. */
export class DiagnosticCancelled extends Error {}

export interface DiagnosticHandlers {
    onStep: (step: DiagnosticStep) => void;
    /** Se consulta en cada muestra: true corta la medicion. */
    isCancelled: () => boolean;
    /** Todas las muestras tomadas hasta ahora, para las metricas en vivo. */
    onSamples?: (samples: Sample[]) => void;
    /** Medicion continua: cada muestra apenas se toma, sin acumularlas. */
    onSample?: (sample: Sample) => void;
    /** Medicion continua: true saltea la muestra (pestaña en segundo plano). */
    isPaused?: () => boolean;
}

const REGISTER_TIMEOUT_MS = 10000;
// JsSIP no envia el INVITE hasta que ICE termina y no tiene timeout propio: en redes
// restrictivas la llamada se quedaria en "marcando" para siempre, asi que se fuerza
// con los candidatos juntados hasta ese momento.
const ICE_WAIT_MS = 2000;
const SAMPLE_EVERY_MS = 1000;
export const DEFAULT_SECONDS = 15;
export const MIN_SECONDS = 5;
// Tope de la primera version: 5 minutos.
export const MAX_SECONDS = 300;
// JsSIP pide 90 s y una PBX con Min-SE mayor responde 422 sin reintento; el webphone usa 6 h.
const SESSION_EXPIRES_S = 21600;
const ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }];

/**
 * Registra la extension de diagnostico, llama al eco y mide `durationSeconds`
 * de audio (una muestra por segundo). Con `Infinity` mide hasta que `isCancelled`
 * devuelva true o la llamada se caiga.
 * Usa un tono sintetico: nunca abre el microfono. Siempre libera el UA, la
 * llamada y el AudioContext, haya salido bien, mal o cancelada.
 */
export async function runDiagnostic(
    config: DiagnosticConfig,
    durationSeconds: number,
    {
        onStep, isCancelled, onSamples, onSample, isPaused,
    }: DiagnosticHandlers,
): Promise<Summary> {
    const cleanups: Array<() => void> = [];

    try {
        onStep('register');
        const JsSIP = await import('jssip');
        const socket = new JsSIP.WebSocketInterface(config.wss_url);
        // mod_sofia empareja sobre "WS" y descarta el REGISTER sin responder si no coincide.
        if (config.wss_url.includes(':7450')) {
            // eslint-disable-next-line @typescript-eslint/camelcase
            (socket as any).via_transport = 'WS';
        }
        const ua = new JsSIP.UA({
            sockets: [socket],
            uri: `sip:${config.user}@${config.domain}`,
            password: config.password,
            // JsSIP reabre el WSS solo (cada 2 a 30 s) y vuelve a registrar: contra un firewall que
            // banea por intentos eso es una rafaga. Los reintentos los maneja quien llama.
            // eslint-disable-next-line @typescript-eslint/camelcase
            connection_recovery_min_interval: 120,
            // eslint-disable-next-line @typescript-eslint/camelcase
            connection_recovery_max_interval: 600,
        });
        cleanups.push(() => ua.stop());

        await new Promise<void>((resolve, reject) => {
            // Sin respuesta: lo ultimo que paso con el WSS distingue "no conecta" de "no contesta".
            let lastCause = 'la central no respondió al registro';
            const timer = window.setTimeout(
                () => reject(new DiagnosticError('register', lastCause)),
                REGISTER_TIMEOUT_MS,
            );
            ua.on('disconnected', (event: any) => {
                // un solo intento por ciclo: sin esperar al timeout ni a la recuperacion de JsSIP
                window.clearTimeout(timer);
                lastCause = `no se pudo abrir el WSS (código ${event?.code ?? '?'})`;
                reject(new DiagnosticError('register', lastCause));
            });
            ua.on('registered', () => {
                window.clearTimeout(timer);
                resolve();
            });
            ua.on('registrationFailed', (event: any) => {
                window.clearTimeout(timer);
                const status = event?.response?.status_code;
                const cause = event?.cause ?? 'rechazado';
                reject(new DiagnosticError('register', status ? `${cause} (SIP ${status})` : cause));
            });
            ua.start();
        });

        onStep('call');
        const audioContext = new AudioContext();
        const destination = audioContext.createMediaStreamDestination();
        const oscillator = audioContext.createOscillator();
        oscillator.connect(destination);
        oscillator.start();
        audioContext.resume();
        cleanups.push(() => { audioContext.close(); });

        let peer: RTCPeerConnection | null = null;
        const samples = await new Promise<Sample[]>((resolve, reject) => {
            let iceTimer: number | null = null;
            const collected: Sample[] = [];
            const session = ua.call(`sip:${config.echo_extension}@${config.domain}`, {
                mediaConstraints: { audio: true, video: false },
                mediaStream: destination.stream,
                pcConfig: { iceServers: ICE_SERVERS },
                sessionTimersExpires: SESSION_EXPIRES_S,
                eventHandlers: {
                    icecandidate: (event: any) => {
                        if (iceTimer === null) {
                            iceTimer = window.setTimeout(() => event.ready(), ICE_WAIT_MS);
                        }
                    },
                    peerconnection: (event: any) => {
                        peer = event.peerconnection as RTCPeerConnection;
                        peer.addEventListener('track', (trackEvent: RTCTrackEvent) => {
                            // En silencio, pero reproducido: asi getStats() reporta contadores reales.
                            const audio = new Audio();
                            audio.muted = true;
                            audio.srcObject = trackEvent.streams[0] ?? null;
                            audio.play().catch(() => undefined);
                        });
                    },
                    confirmed: () => {
                        onStep('measure');
                        const timer = window.setInterval(async () => {
                            if (isCancelled()) {
                                window.clearInterval(timer);
                                reject(new DiagnosticCancelled());
                                return;
                            }
                            if (!peer) {
                                return;
                            }
                            if (isPaused && isPaused()) {
                                return;
                            }
                            const sample = readSample(await peer.getStats());
                            if (onSample) {
                                onSample(sample);
                            }
                            // con duracion infinita no se acumula: la medicion continua usaria memoria sin tope
                            if (!Number.isFinite(durationSeconds)) {
                                return;
                            }
                            collected.push(sample);
                            if (onSamples) {
                                onSamples([...collected]);
                            }
                            if (collected.length >= durationSeconds) {
                                window.clearInterval(timer);
                                resolve(collected);
                            }
                        }, SAMPLE_EVERY_MS);
                        cleanups.push(() => window.clearInterval(timer));
                    },
                    failed: (event: any) => reject(new DiagnosticError('call', event?.cause ?? '')),
                    ended: (event: any) => reject(new DiagnosticError('call', event?.cause ?? '')),
                },
            });
            cleanups.push(() => {
                if (!session.isEnded()) {
                    session.terminate();
                }
            });
        });

        return summarize(samples);
    } finally {
        cleanups.splice(0).forEach((cleanup) => cleanup());
    }
}
